<template>
  <div
    class="py-6 px-6 bg-pure-white rounded-xl"
    :style="{ height: '12.5rem', width: '51.25rem' }"
  >
    <div class="mb-1 flex items-center justify-between">
      <BlTextTitle4 text="Prospects by stage" class="text-slate-900" />
      <router-link
        :to="
          `/analytics/acquisition-overview?start_date=${dateInput.model[0].start_date}&end_date=${dateInput.model[0].end_date}`
        "
      >
        <BlInputButton type="outlined" dense label="Details" />
      </router-link>
    </div>
    <div class="mb-5 flex items-center gap-2">
      <BlInputFilter
        :activeLabel="dateActiveLabel"
        title="Date"
        :defaultValue="[defaultDate]"
        :input="dateInput"
        @changed="onChangeHandler"
      />
    </div>
    <div v-if="fetchLoading" class="h-16 grid place-content-center">
      <div class="blum-loader-sm"></div>
    </div>
    <div v-else-if="!graphData" class="h-16 grid place-content-center">
      <BlTextBody2 :text="`Data not found.`" />
    </div>
    <div v-else class="flex-grow flex-shrink-0 overflow-x-auto scrollbar-lg">
      <div class="pb-1 flex gap-7">
        <div
          v-for="item in overviewData"
          :key="item.label"
          class="relative h-16 flex items-center flex-shrink-0"
          :style="{
            width: `calc((100% - 3rem - (1.875rem * ${overviewData.length - 1})) / 6)`
          }"
          :title="item.label"
        >
          <BlTooltip class="w-full flex flex-col">
            <BlTextTitle3
              class="self-center"
              :class="{
                'text-cornflower-800':
                  !item.is_admitted &&
                  !item.is_pending &&
                  !item.is_discontinued,
                'text-lime-600': item.is_admitted,
                'text-tulip-600': item.is_pending,
                'text-rose-700': item.is_discontinued
              }"
              :text="item.value"
            />
            <div class="text-center truncate">
              <BlTextBody2 :text="item.label" class="truncate" />
            </div>
          </BlTooltip>
          <div v-if="item.divider" class="absolute -right-3.5">
            <div class="h-16 w-0.5 flex-shrink-0 bg-slate-300"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextTitle3 from "../../BlTextTitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTooltip from "../../BlTooltip.vue";
import BlInputFilter from "../../BlInputFilter.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "CenterOverviewProspectsByStage",
  components: {
    BlTextTitle4,
    BlTextTitle3,
    BlTextBody2,
    BlTooltip,
    BlInputFilter,
    BlInputButton
  },
  data() {
    const interval = "year";
    const defaultDate = {
      start_date: moment()
        .startOf(interval)
        .format("YYYY-MM-DD"),
      end_date: moment()
        .endOf(interval)
        .format("YYYY-MM-DD")
    };

    return {
      defaultDate,
      /** */
      fetchLoading: false,
      graphData: null,
      overviewData: [],
      /** */
      dateInput: {
        model: [defaultDate],
        tabs: [{ name: "Date", kind: "date" }],
        dense: true
      }
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    searchParams() {
      return this.$route.query || {};
    },
    dateActiveLabel() {
      const start = this.dateInput.model[0].start_date;
      const end = this.dateInput.model[0].end_date;

      const sYYYY = moment(start).format("YYYY");
      const sMMM = moment(start).format("MMM");
      const sDD = moment(start).format("DD");

      const eYYYY = moment(end).format("YYYY");
      const eMMM = moment(end).format("MMM");
      const eDD = moment(end).format("DD");

      let date = "";
      if (sYYYY === eYYYY) {
        if (sMMM === eMMM) date = `${sDD} - ${eDD} ${sMMM}, ${sYYYY}`;
        else if (sMMM === "Jan" && eMMM === "Dec") date = `${sYYYY}`;
        else date = `${sDD} ${sMMM} - ${eDD} ${eMMM}, ${sYYYY}`;
      } else date = `${sYYYY} - ${eYYYY}`;
      return `
        <span class="flex items-center gap-2">
          <span>Date</span>
          <b>${date}</b>
        </span>
      `;
    }
  },
  mounted() {
    this.fetchGraph();
  },
  methods: {
    ...mapActions(["actionGet"]),
    fetchGraph() {
      const start = this.dateInput.model[0].start_date;
      const end = this.dateInput.model[0].end_date;
      let params = `?start_date=${start}&end_date=${end}`;
      if (this.searchParams["branch"]) {
        params += `&branch_ids=${this.searchParams["branch"]}`;
      }
      const data = {
        url: `/api/v1/analytics/acquisition_overview_graph${params}`
      };
      this.fetchLoading = true;
      this.graphData = null;
      this.actionGet(data).then(({ success, data }) => {
        this.fetchLoading = false;
        if (!success) return;
        this.graphData = data;
        this.generateOverview(data.overview || []);
      });
    },
    generateOverview(arr) {
      /*
      "overview": [
        { "stage_name": "Inquiry", "value": 11 },
        { "stage_name": "Consultation", "value": 8 },
        { "stage_name": "Assessment", "value": 0 },
        { "stage_name": "Admitted", "value": 9 },
        { "stage_name": "Pending", "value": 3 },
        { "stage_name": "Discontinued", "value": 5 }
      ]
      */
      this.overviewData = [];
      if (!arr) return [];
      this.overviewData = arr.map((i, idx) => ({
        label: i.stage_name,
        value: i.value,
        is_admitted: i.stage_name === "Admitted",
        is_pending: i.stage_name === "Pending",
        is_discontinued: i.stage_name === "Discontinued",
        selected: true,
        divider: idx !== Object.keys(arr).length - 1
      }));
    },
    onChangeHandler(payload) {
      payload.input.model = payload.value;
      this.fetchGraph();
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/analytics/mainDashboard/ProspectsByStage.vue